import { THEME_DARK } from "@hoylu/client-common";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import type { AppState } from "../../state/root.reducer";
import {
  selectWorkspaceById,
  selectWorkspacesLoadProgress,
  selectWorkspacesLoadState,
  selectWorkspacesWaitingToEditId,
} from "../../state/workspaces/workspaces.selector";
import { useI18n } from "../../utils/hooks/use.i18n";
import { LdsRoller } from "./LdsRoller";
import { Modal } from "./Modal";
import Styles from "./WorkspaceDialog.module.css";

function formatWorkspaceId(id: string | null) {
  return id ? id.match(/.{1,3}/g)?.join("-") : null;
}

export const LoadingWorkspaceDialog = () => {
  const t = useI18n("LOADING_WORKSPACE_DIALOG.");

  let workspaceId = useSelector(selectWorkspacesWaitingToEditId);
  workspaceId ??= window.location.pathname.substring(1);
  const workspace = useSelector((state: AppState) =>
    selectWorkspaceById(state, workspaceId),
  );
  const progress = useSelector(selectWorkspacesLoadProgress);
  const step = useSelector(selectWorkspacesLoadState);

  const [delayLoadingPercent, setDelayLoadingPercent] = useState(false);
  const [showLoadingPercent, setShowLoadingPercent] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDelayLoadingPercent(true), 8000);
    // NOTE: Users seems to give up on page after 10s according to 06.2021 research :)
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setShowLoadingPercent(
      delayLoadingPercent && (!!Math.floor(progress) || !!step),
    );
  }, [delayLoadingPercent, progress, step]);

  const loadStage = useMemo(() => {
    switch (step) {
      case 1:
        return t("PROCESS");
      case 2:
        return t("RENDER");
      case 0:
      default:
        return t("LOADED");
    }
  }, [step, t]);

  const title = workspace?.workspaceName || formatWorkspaceId(workspaceId);

  return (
    <Modal forcedTheme={THEME_DARK}>
      <div className={Styles.loadingWorkspaceDialog}>
        <div className={Styles.header}>
          {t("LOADING_WORKSPACE")}
          <span className={Styles.title}>
            <span>&quot;</span>
            {title}
            <span>&quot;</span>
          </span>
        </div>
        <LdsRoller />
        {showLoadingPercent && (
          <>
            <div className={Styles.loading}>{t("STEP", step + 1, 3)}</div>
            <div className={Styles.loading}>
              {Math.floor(progress)}
              <span>%</span> {loadStage}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default LoadingWorkspaceDialog;
